// Delete my account (FR-32): irreversible. The person types a confirmation
// word in their language, confirms with step-up "delete", and the server
// erases the profile and ends every session. The local session is cleared by
// AccountDeletedPage once it has rendered (as with suspension).
import { Button, Group, List, Modal, Paper, Stack, Text, TextInput, Title } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { api } from "../../api/client";
import { errorMessage } from "../../shared/session/errors";
import { useStepUp } from "../../shared/session/stepup";
import { FormError } from "../../shared/ui/Page";

export function DeleteSection() {
  const { t, i18n } = useTranslation();
  const stepUp = useStepUp();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const word = t("delete_account.word");
  const matches = typed.trim().toLocaleUpperCase(i18n.language) === word.toLocaleUpperCase(i18n.language);

  const close = () => {
    setConfirming(false);
    setTyped("");
  };
  const remove = async () => {
    if (!matches) return;
    close();
    setError(null);
    const token = await stepUp("delete");
    if (!token) return;
    setBusy(true);
    const { response, error } = await api.POST("/me/delete", { params: { header: { "X-Step-Up-Token": token } } });
    setBusy(false);
    if (!response.ok) return setError(errorMessage(error, t("error.generic")));
    navigate("/account-deleted", { replace: true, state: { signOut: true } });
  };

  return (
    <Paper
      withBorder
      p="md"
      radius="md"
      component="section"
      aria-labelledby="delete-title"
      data-testid="delete-section"
      style={{ borderColor: "var(--mantine-color-red-6)" }}
    >
      <Stack gap="sm">
        <Title order={2} size="h4" id="delete-title">
          {t("delete_account.title")}
        </Title>
        <Text size="sm" fw={600}>
          {t("delete_account.intro")}
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>{t("delete_account.point_code")}</List.Item>
          <List.Item>{t("delete_account.point_businesses")}</List.Item>
          <List.Item>{t("delete_account.point_records")}</List.Item>
          <List.Item>{t("delete_account.point_export")}</List.Item>
        </List>
        <Button color="red" w="fit-content" loading={busy} onClick={() => setConfirming(true)}>
          {t("delete_account.button")}
        </Button>
        <FormError message={error} />
      </Stack>
      <Modal
        opened={confirming}
        onClose={close}
        title={t("delete_account.modal_title")}
        centered
        closeButtonProps={{ "aria-label": t("common.close") }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void remove();
          }}
          data-testid="delete-confirm"
        >
          <Stack gap="sm">
            <Text size="sm">{t("delete_account.modal_body")}</Text>
            <TextInput
              label={t("delete_account.confirm_label", { word })}
              value={typed}
              onChange={(e) => setTyped(e.currentTarget.value)}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              data-autofocus
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={close}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" color="red" disabled={!matches}>
                {t("delete_account.confirm")}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Paper>
  );
}
